import { finalize } from "rxjs/operators";
import { Subscription } from "rxjs";
import { OnDestroy, Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AmericanState } from "src/app/enums/american-state";
import { ModalName } from "src/app/enums/modal-name";
import { StorageKey } from "src/app/enums/storage-key";
import { Timezone } from "src/app/enums/timezone";
import { IAddClientInfoPayload } from "src/app/models/client/add-client-info-payload";
import { IClientProfileInfo } from "src/app/models/client/client-info";
import { IClientNumber } from "src/app/models/client/client-number";
import { IPaymentLink } from "src/app/models/client/payment-link";
import { IUpdateClientInfoPayload } from "src/app/models/client/update-client-info-payload";
import { ICoverPage } from "src/app/models/cover-page/cover-page";
import { ClientService } from "src/app/services/client.service";
import { CoverPageService } from "src/app/services/cover-page.service";
import { ModalService } from "src/app/services/modal.service";
import { LocalStorage } from "src/libs/storage/src";

@Component({
  selector: "app-profile",
  templateUrl: "./profile.component.html",
  styleUrls: ["./profile.component.scss"],
})
export class ProfileComponent implements OnDestroy, OnInit {
  private _numbersSubscription?: Subscription;
  clientProfileInfo: IClientProfileInfo | null = null;
  isClientProfileInfoLoading = true;
  isCoverPageSaving = false;
  coverPages: ICoverPage[] = [];
  coverPagesPageNumber = "1";
  selectedCoverPage: ICoverPage | null = null;

  clientNumbers: IClientNumber[] = [];

  successModalText = "Your purchase was successful";
  successModalButtonText = "Close";
  readonly MissingProfileInfo = ModalName.MissingProfileInfo;

  private readonly _pageSize = "10";

  constructor(
    private readonly _clientService: ClientService,
    private readonly _coverPageService: CoverPageService,
    private readonly _modalService: ModalService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _localStorage: LocalStorage,
    private readonly _router: Router
  ) {}

  get primaryClientNumber() {
    return this.clientNumbers.find(number => number.isPrimary)?.number ?? "*missing*";
  }

  addOrUpdateClientInfo(payload: IUpdateClientInfoPayload) {
    if (this.clientProfileInfo === null) {
      this.addClientInfo(payload);
    } else {
      this.updateClientInfo(payload);
    }
  }

  private addClientInfo(payload: IUpdateClientInfoPayload) {
    const addClientInfoPayload: IAddClientInfoPayload = {
      companyName: payload.companyName ?? "",
      firstName: payload.firstName ?? "",
      lastName: payload.lastName ?? "",
      billingEmail: payload.billingEmail ?? "",
      phoneNumber: payload.phoneNumber ?? "",
      address: payload.address ?? "",
      city: payload.city ?? "",
      state: payload.state ?? AmericanState.Florida,
      country: payload.country ?? "",
      zipCode: payload.zipCode ?? "",
      shippingCity: "*missing*",
      shippingState: AmericanState.Florida,
      shippingAddress: "*missing*",
      shippingCountry: "*missing*",
      timeZone: payload.timeZone ?? Timezone.Eastern,
    };

    this._clientService.addClientInfo(addClientInfoPayload).subscribe({
      next: () => {
        this.getClientProfileInfo();
        this.openBuyClientNumberModalIfPending();
      },
    });
  }

  private updateClientInfo(payload: IUpdateClientInfoPayload) {
    this._clientService.updateClientInfo(payload).subscribe({
      next: () => {
        this.getClientProfileInfo();
        this.openBuyClientNumberModalIfPending();
      },
    });
  }

  // The free fax number is issued only after the profile info is filled
  private openBuyClientNumberModalIfPending() {
    if (this._localStorage.getItem(StorageKey.IsPendingFaxNumberAfterProfile) !== "true") {
      return;
    }

    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: { fillProfile: null, profileRequired: null },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
    this._localStorage.removeItem(StorageKey.IsPendingFaxNumberAfterProfile);
    this._modalService.openModal(ModalName.BuyClientNumber);
  }

  changePrimaryClientNumber(number: string) {
    this._clientService.changePrimaryNumber({ number }).subscribe({
      next: clientNumber => this.setNewPrimaryClientNumber(clientNumber),
    });
  }

  private setNewPrimaryClientNumber(newPrimaryClientNumber: IClientNumber) {
    this.clientNumbers = this.clientNumbers.map(clientNumber => {
      if (clientNumber.isPrimary) {
        return { ...clientNumber, isPrimary: false };
      }
      if (clientNumber.number === newPrimaryClientNumber.number) {
        return newPrimaryClientNumber;
      }
      return clientNumber;
    });
  }

  openBuyClientNumberModal() {
    this._modalService.openModal(ModalName.BuyClientNumber);
  }

  openEditClientInfoModal() {
    this._modalService.openModal(ModalName.EditClientInfo);
  }

  openCreateCoverPageModal() {
    this._modalService.openModal(ModalName.CreateCoverPage);
  }

  openEditCoverPageModal(coverPage: ICoverPage) {
    this.selectedCoverPage = coverPage;
    this._modalService.openModal(ModalName.EditCoverPage);
  }

  openDeleteCoverPageModal(coverPage: ICoverPage) {
    this.selectedCoverPage = coverPage;
    this._modalService.openModal(ModalName.DeleteCoverPage);
  }

  createAndOpenClientNumberPaymentLink(paymentLink: IPaymentLink | null) {
    if (!paymentLink) {
      this._localStorage.setItem(StorageKey.IsPendingFaxNumberPurchase, "true");
      this.openFaxNumberAddedOrPurchaseSuccessModal();
      return;
    }

    window.open(paymentLink.url, "_blank", "noreferrer");
  }

  createCoverPage(payload: ICoverPage) {
    this._coverPageService.create(payload).subscribe({
      next: () => this.getCoverPages(),
    });
  }

  updateCoverPage(payload: Partial<ICoverPage>) {
    if (this.isCoverPageSaving) {
      return;
    }
    const coverPage = this.selectedCoverPage;

    if (coverPage !== null) {
      this.isCoverPageSaving = true;
      this._coverPageService.update({ oldTitle: coverPage.title }, payload)
        .pipe(finalize(() => this.isCoverPageSaving = false))
        .subscribe({
          next: () => {
            this._modalService.closeModal();
            this.getCoverPages();
          },
        });
    }
  }

  deleteCoverPage() {
    const coverPage = this.selectedCoverPage;

    if (coverPage !== null) {
      this._coverPageService.delete({ title: coverPage.title }).subscribe({
        next: () => this.getCoverPages(),
      });
    }
  }

  ngOnInit() {
    this.getClientProfileInfo(true);
    this.getCoverPages();
    this.getClientNumbers();
  }

  private getClientProfileInfo(openQueryModal = false) {
    this._clientService.getClientInfo().subscribe(clientInfo => {
      this.clientProfileInfo = clientInfo.profileInfo;
      this.isClientProfileInfoLoading = false;
      if (openQueryModal) {
        this.openModalIfQueryParamsExist();
      }
    });
  }

  private getCoverPages() {
    this._coverPageService
      .getAll({ pageNumber: this.coverPagesPageNumber, pageSize: this._pageSize })
      .subscribe(coverPages => {
        this.coverPages = coverPages.records;
      });
  }

  private getClientNumbers() {
    this._numbersSubscription = this._clientService.watchNumbers().subscribe(numbers => {
      this.clientNumbers = numbers;
    });
  }

  private openModalIfQueryParamsExist() {
    const params = this._activatedRoute.snapshot.queryParams;

    if (params.payment === "success") {
      this.openFaxNumberAddedOrPurchaseSuccessModal();
    }

    if (params.fillProfile === "true" && !this.clientProfileInfo) {
      this.openEditClientInfoModal();
    }

    if (params.profileRequired === "true" && !this.clientProfileInfo) {
      this._modalService.openModal(ModalName.MissingProfileInfo);
    }

    if (params.payment === "failure") {
      this._modalService.openModal(ModalName.Error);
    }
  }

  private openFaxNumberAddedOrPurchaseSuccessModal() {
    const isPendingFaxNumberPurchase =
      this._localStorage.getItem(StorageKey.IsPendingFaxNumberPurchase) === "true";

    if (isPendingFaxNumberPurchase) {
      this._localStorage.removeItem(StorageKey.IsPendingFaxNumberPurchase);
      this.successModalText = "Fax number is successfully added!";
      this.successModalButtonText = "Close";
    } else {
      this.successModalText = "Your purchase was successful";
      this.successModalButtonText = "Close";
    }

    this._modalService.openModal(ModalName.Success);
  }

  ngOnDestroy() {
    this._numbersSubscription?.unsubscribe();
  }
}
